import React, { useState } from 'react';
import { Folder, FilePlus2 } from 'lucide-react';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import SidebarLayout from '@/components/layout/SidebarLayout';
import WorkbookSidebar from '@/components/content-generator/workbook/WorkbookSidebar';
import WorkbookMainContent from '@/components/content-generator/workbook/WorkbookMainContent';
import { WorkbookProvider, useWorkbook } from '@/contexts/workbook/WorkbookContext';
import MobilePageWrapper from '@/components/ui/MobilePageWrapper';
import NewWorkbookModal from '@/components/content-generator/workbook/NewWorkbookModal';
import { TagFormDialog } from '@/components/content-generator/workbook';
import { useAutoNavigation } from '@/hooks/nav/use-auto-navigation';
import { getAppDescription } from '@/config/app-registry';
import { getWorkbooks } from '@/hooks/content-generator/workbook-operations';
import { useEffect } from 'react';
import { useQuestDetection } from '@/hooks/onboarding/useQuestDetection';


const ContentGeneratorInner: React.FC = () => {
  const { openCreateProjectModal, isCreateProjectModalOpen, closeCreateProjectModal, isCreateTagModalOpen, closeCreateTagModal, filterStatus, selectedTagId, searchQuery } = useWorkbook();
  const [isNewModalOpen, setNewModalOpen] = useState(false);
  const [workbookCount, setWorkbookCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { goToWorkbookEditor, goToTaskManager } = useAutoNavigation();
  
  // Initialize quest detection for this page
  useQuestDetection();
  
  // 🤖 AI CONTEXT: Cache now populated by database-driven system in AuthProvider

  // Track project count for header visibility
  useEffect(() => {
    const loadWorkbookCount = async () => {
      try {
        setIsLoading(true);
        const result = await getWorkbooks({
          filterByStatus: filterStatus,
          filterByTag: selectedTagId || undefined,
          searchQuery: searchQuery
        });
        setWorkbookCount(result.workbooks?.length || 0);
      } catch (error) {
        console.error('Failed to load workbook count:', error);
        setWorkbookCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkbookCount();
  }, [filterStatus, selectedTagId, searchQuery]);

  // Show different layouts based on whether user has projects
  const hasProjects = workbookCount > 0;
  const shouldShowCenteredLayout = !isLoading && !hasProjects && filterStatus === 'all' && !selectedTagId && !searchQuery;

  const handleProjectCreated = (workbookId: string, goalId: string) => {
    // Navigate to the specific project's task manager
    goToTaskManager(goalId, workbookId);
  };

  return (
    <MobilePageWrapper>
      <Container>
        {!shouldShowCenteredLayout && (
          <DashboardHeader
            title="Project Manager"
            icon={<Folder size={24} />}
            description={getAppDescription('content-generator')}
          />
        )}
        
        <div className="space-y-3 md:space-y-6">
          {shouldShowCenteredLayout ? (
            <WorkbookMainContent 
              isLoading={isLoading}
              workbookCount={workbookCount}
              shouldShowCenteredLayout={true}
            />
          ) : (
            <SidebarLayout
              sidebar={<WorkbookSidebar />}
            >
              <WorkbookMainContent 
                isLoading={isLoading}
                workbookCount={workbookCount}
                shouldShowCenteredLayout={false}
              />
            </SidebarLayout>
          )}
        </div>
      </Container>
      
      {/* New Workbook Modal - Moved to parent level to prevent unmounting during layout changes */}
      <NewWorkbookModal
        isOpen={isNewModalOpen}
        onClose={() => setNewModalOpen(false)}
        onProjectCreated={handleProjectCreated}
      />
      
      {/* Create Project Modal - From context state */}
      <NewWorkbookModal
        isOpen={isCreateProjectModalOpen}
        onClose={closeCreateProjectModal}
        onProjectCreated={handleProjectCreated}
      />
      
      {/* Tag Creation Modal */}
      <TagFormDialog
        isOpen={isCreateTagModalOpen}
        onClose={closeCreateTagModal}
        mode="create"
      />
    </MobilePageWrapper>
  );
};

const ContentGenerator: React.FC = () => {
  return (
    <WorkbookProvider>
      <ContentGeneratorInner />
    </WorkbookProvider>
  );
};

export default ContentGenerator;
